import SelectInput from "@/components/inputs/SelectInput";
import { OperationFormType } from "@/lib/form/operation";
import { FileProperty } from "@/types/file";
import { faClose, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addToast, Button, Divider } from "@heroui/react";
import { useCallback, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import shp, { FeatureCollectionWithFilename } from "shpjs";
import { useTranslation } from "react-i18next";
import { filtetShpFile } from "@/lib/utils/file";

function ShapefileUpload({
    fileInputProperties,
    setFile,
}: {
    fileInputProperties: FileProperty[];
    setFile: (file?: File | null) => void;
}) {
    const { t } = useTranslation("messages");
    const f = useFormContext<OperationFormType>();
    const [label, setLabel] = useState<string>("Upload File");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const fileUpload = useRef<File>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [shapefileProperties, setShapefileProperties] = useState<string[]>(
        [],
    );

    const handleFileSelected = useCallback(
        async (file: File | undefined) => {
            if (!file) return;
            try {
                fileUpload.current = file;
                const fileData = await filtetShpFile(file);
                const fileGeoJson: FeatureCollectionWithFilename = (await shp(
                    fileData,
                )) as FeatureCollectionWithFilename;
                setShapefileProperties(
                    Object.keys(fileGeoJson?.features[0]?.properties ?? {}),
                );
                f.setValue("properties", fileInputProperties);
                setFile(file);
                setSelectedFile(file);
                setLabel(file.name);
            } catch {
                addToast({
                    title: t("addToast.invalid_shapefile"),
                    timeout: 5000,
                    color: "danger",
                });
                setFile(null);
                setSelectedFile(null);
                setLabel("Upload File");
                f.setValue("properties", []);
            }
        },
        [f, fileInputProperties, setFile, t],
    );

    const { fields } = useFieldArray<OperationFormType, "properties">({
        control: f.control,
        name: "properties",
    });

    return (
        <>
            <div className="flex flex-row gap-2">
                <div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => {
                            handleFileSelected(e.target.files?.[0]);
                        }}
                        className="hidden"
                    />
                    <Button
                        onPress={() => {
                            fileInputRef.current?.click();
                        }}
                        color="primary"
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faUpload} />
                        {label ?? "Upload"}
                    </Button>
                </div>
                {selectedFile && (
                    <Button
                        color="danger"
                        size="sm"
                        onPress={() => {
                            setFile(null);
                            setSelectedFile(null);
                            setLabel("Upload File");
                            setShapefileProperties([]);
                            f.setValue("properties", []);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faClose} />
                        Remove File
                    </Button>
                )}
            </div>
            {shapefileProperties.length > 0 && (
                <div className="flex flex-col gap-2">
                    <div className="font-semibold">File Properties</div>
                    {fields?.map((property, index) => {
                        return (
                            <div className="flex flex-row gap-2">
                                <input
                                    type="hidden"
                                    name={`properties.${index}.required`}
                                    value={property.required?.toString()}
                                />
                                <input
                                    type="hidden"
                                    name={`properties.${index}.label`}
                                    value={property.label}
                                />
                                <SelectInput
                                    hideErrorMessage
                                    name={`properties.${index}.file_label`}
                                    label={property.label}
                                    isRequired
                                    labelPlacement="outside"
                                    size="sm"
                                    placeholder="Select Property"
                                    options={shapefileProperties.map(
                                        (property) => ({
                                            id: property,
                                            description: property,
                                        }),
                                    )}
                                />
                                <SelectInput
                                    hideErrorMessage
                                    name={`properties.${index}.unit`}
                                    label="Unit"
                                    isRequired
                                    size="sm"
                                    labelPlacement="outside"
                                    placeholder="Select Unit"
                                    options={
                                        property.unit_options?.map((unit) => ({
                                            id: unit ?? "",
                                            description: unit ?? "",
                                        })) ?? []
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            <Divider />
        </>
    );
}

export default ShapefileUpload;
